import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, FileSearch, Clock } from 'lucide-react';
import { getDPDPAAudit } from '@/services/dpdpa';

const actionColor = (action) => {
  if (action.includes('created')) return 'bg-emerald-500/10 text-emerald-700';
  if (action.includes('withdrawn') || action.includes('deleted')) return 'bg-red-500/10 text-red-700';
  if (action.includes('updated') || action.includes('changed')) return 'bg-blue-500/10 text-blue-700';
  if (action.includes('scan')) return 'bg-violet-500/10 text-violet-700';
  return 'bg-gray-500/10 text-gray-700';
};

export default function DPDPAAuditTrailPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAudit = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getDPDPAAudit(200);
      setEntries(res.entries || []);
    } catch (err) {
      console.error('Failed to load audit trail:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin opacity-50" /></div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="dpdpa-audit-trail-page">
      <div>
        <h1 className="text-2xl font-bold">DPDPA Audit Trail</h1>
        <p className="text-sm opacity-60">Complete audit log of all consent and data protection activities</p>
      </div>

      {entries.length === 0 ? (
        <Card><CardContent className="p-8 text-center opacity-50"><FileSearch className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>No audit entries yet. Activities will be logged as you use the Consent Manager.</p></CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="audit-trail-table">
                <thead>
                  <tr className="border-b text-left">
                    <th className="p-3 font-medium opacity-60">Timestamp</th>
                    <th className="p-3 font-medium opacity-60">Action</th>
                    <th className="p-3 font-medium opacity-60">Details</th>
                    <th className="p-3 font-medium opacity-60">Actor</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr key={entry.id} className="border-b hover:bg-black/5 transition-colors">
                      <td className="p-3 text-xs whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3 opacity-40" />
                          {new Date(entry.timestamp).toLocaleString()}
                        </div>
                      </td>
                      <td className="p-3">
                        <Badge className={`text-xs ${actionColor(entry.action)}`}>
                          {entry.action.replace(/_/g, ' ')}
                        </Badge>
                      </td>
                      <td className="p-3 max-w-md truncate opacity-70">{entry.details}</td>
                      <td className="p-3 text-xs">{entry.actor}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
